/**
 * Storage path → download URL の localStorage backed キャッシュ (docs/106 Approach 2)。
 *
 * 目的: `usePhotoUrl` の path→URL キャッシュをリロード後も保持し、オフラインで
 * `getDownloadURL` が失敗しても「最後に取得した URL」で写真を再表示できるようにする。
 * URL の bytes 自体は SW (CacheFirst) が保持しているため、URL 文字列さえ復元できれば
 * `<img src>` がキャッシュから描画される。
 *
 * 設計判断 (docs/106 §3-5):
 *   - 保存対象は path→URL の **小さな文字列のみ** → IndexedDB ではなく localStorage を採用
 *     (同期アクセスで hook が単純、i18n が既に `cw.locale` で localStorage を使用済)。
 *   - IndexedDB は写真 Blob 用途の outbox に限定する。
 *
 * キー規約: `cw.photoUrl:<path>` (1 path = 1 entry)。簡易 LRU で件数上限を超えたら
 * 最も古い (= 最後に touch されてから最長) entry を削除する。
 *
 * 全 API は localStorage 不在 / 例外 (容量超過, SSR, privacy mode) に対し silent fallback し、
 * 例外を投げない (写真表示は best-effort であり cache 失敗で UI を壊さない)。
 */

/** 1 entry あたりの key prefix。 */
const KEY_PREFIX = 'cw.photoUrl:';
/** LRU の touch 時刻を記録する別 index key (path → lastAccessMs の JSON)。 */
const INDEX_KEY = 'cw.photoUrlIndex';
/**
 * 保持上限 (超過時に LRU eviction)。1 回のイベントで約 500 投稿 × (サムネ + 原寸) が集まり、
 * 複数のイベントを行う地区もあるため 3000 パス (2026-10-09、旧 500)。1 entry は URL 2 本程度
 * (約 0.6KB) + index 約 0.1KB で、3000 件で約 2MB (localStorage の上限 約 5MB の範囲内)。
 */
export const MAX_PHOTO_URL_ENTRIES = 3000;
/**
 * docs/28: 値は `url` または `url` + DOC_SEP + `そのときの投稿 doc の URL`。
 * 旧形式 (url のみ) もそのまま読める。改行は URL に含まれない。
 */
const DOC_SEP = '\n';

function getLs(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    // SecurityError (一部 privacy mode で window.localStorage access が throw) 等。
    return null;
  }
}

/**
 * path → lastAccessMs の LRU index。メモリに持ち、localStorage への書き戻しはまとめて行う。
 * 書き戻すときは localStorage の index を読み直して合わせる (複数タブが同時に開いていても、
 * ほかのタブが足した entry を index から落とさない、docs/28 レビュー A3)。このタブで消した
 * entry は removedInTab で覚えて、合わせるときに戻さない。
 */
let memIndex: Record<string, number> | null = null;
const removedInTab = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function loadIndex(ls: Storage): Record<string, number> {
  if (memIndex) return memIndex;
  try {
    const raw = ls.getItem(INDEX_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    memIndex =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, number>)
        : {};
  } catch {
    memIndex = {};
  }
  // index から漏れた entry (タブを閉じて書き戻しが間に合わなかった等) は、いちばん古い扱いで
  // index に戻す (件数の上限で先に消える。上限が効かずに保存領域があふれるのを防ぐ)。
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      const path = key.slice(KEY_PREFIX.length);
      if (memIndex[path] === undefined) memIndex[path] = 0;
    }
  } catch {
    // 走査の失敗は無視。
  }
  return memIndex;
}

function flushIndex(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  const ls = getLs();
  if (!ls || !memIndex) return;
  try {
    let stored: Record<string, number> = {};
    try {
      const raw = ls.getItem(INDEX_KEY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : {};
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        stored = parsed as Record<string, number>;
      }
    } catch {
      stored = {};
    }
    const merged: Record<string, number> = {};
    for (const [k, v] of Object.entries(stored)) {
      if (!removedInTab.has(k) && ls.getItem(KEY_PREFIX + k) != null) merged[k] = v;
    }
    for (const [k, v] of Object.entries(memIndex)) {
      merged[k] = Math.max(v, merged[k] ?? 0);
    }
    memIndex = merged;
    removedInTab.clear();
    ls.setItem(INDEX_KEY, JSON.stringify(merged));
  } catch {
    // 書込失敗は無視 (best-effort)。
  }
}

/**
 * index の書き戻しは 1 秒にまとめる (一覧で数百枚を表示するたびに数千件の JSON を書き直さない)。
 * ページを離れる・隠れるときにも書き戻す。
 */
function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(flushIndex, 1000);
}
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushIndex);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushIndex();
  });
}

function readRaw(path: string): string | null {
  const ls = getLs();
  if (!ls || !path) return null;
  try {
    const raw = ls.getItem(KEY_PREFIX + path);
    if (raw == null) return null;
    // LRU touch (メモリのみ、書き戻しはまとめて)。
    loadIndex(ls)[path] = Date.now();
    scheduleFlush();
    return raw;
  } catch {
    return null;
  }
}

/**
 * キャッシュ済 URL を取得 (なければ undefined)。副作用で LRU touch (lastAccess 更新)。
 */
export function getCachedPhotoUrl(path: string): string | undefined {
  const raw = readRaw(path);
  if (!raw) return undefined;
  return raw.split(DOC_SEP)[0] || undefined;
}

/**
 * docs/28: キャッシュ済 URL と、保存したときの投稿 doc の URL (旧形式で保存されたものは undefined)。
 */
export function getCachedPhotoEntry(
  path: string,
): { url: string; docUrl: string | undefined } | undefined {
  const raw = readRaw(path);
  if (!raw) return undefined;
  const [url, docUrl] = raw.split(DOC_SEP);
  return url ? { url, docUrl: docUrl || undefined } : undefined;
}

/**
 * URL をキャッシュに保存 (件数上限超過なら LRU eviction)。
 * 空 path / 空 url は no-op。`docUrl` は保存したときの投稿 doc の URL (docs/28)。
 */
export function setCachedPhotoUrl(path: string, url: string, docUrl?: string | null): void {
  if (!path || !url) return;
  const ls = getLs();
  if (!ls) return;
  try {
    ls.setItem(KEY_PREFIX + path, docUrl ? `${url}${DOC_SEP}${docUrl}` : url);
    const index = loadIndex(ls);
    index[path] = Date.now();

    // 件数上限超過: 最も古い (lastAccess 最小) entry から削除して上限に収める。
    const paths = Object.keys(index);
    if (paths.length > MAX_PHOTO_URL_ENTRIES) {
      const sorted = paths.sort((a, b) => (index[a] ?? 0) - (index[b] ?? 0));
      const evictCount = paths.length - MAX_PHOTO_URL_ENTRIES;
      for (let i = 0; i < evictCount; i += 1) {
        const victim = sorted[i];
        if (victim === undefined) continue;
        try {
          ls.removeItem(KEY_PREFIX + victim);
        } catch {
          // 個別削除失敗は無視。
        }
        delete index[victim];
        removedInTab.add(victim);
      }
    }
    scheduleFlush();
  } catch {
    // QuotaExceededError 等は無視 (cache は best-effort)。容量超過に備え一度全 evict を試す。
    try {
      __clearPhotoUrlStore();
    } catch {
      /* even clear failed — give up silently */
    }
  }
}

/** test / 容量回復用に全 photoUrl entry + index を localStorage から削除する。 */
export function __clearPhotoUrlStore(): void {
  const ls = getLs();
  if (!ls) return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (key && key.startsWith(KEY_PREFIX)) toRemove.push(key);
    }
    for (const key of toRemove) ls.removeItem(key);
    ls.removeItem(INDEX_KEY);
    memIndex = {};
    removedInTab.clear();
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  } catch {
    // 無視。
  }
}
